import { useRequest } from 'ahooks';
import { useEffect, useMemo, useState, createContext, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RegionCountsMap, getRegionsStoreCount } from 'services/api/admin';
import { NimSystemEventStreamMessage } from 'services/nim/eventBus';
import { toUpperCase } from 'utils/toLowerCase';

export const PlatformCtx = createContext<General.PlatformUpperCase | undefined>(undefined);

export const usePlatform = () => {
  return useContext(PlatformCtx);
};

const usePlatformList = () => {
  const location = useLocation();
  const locationStateRef = useRef<NimSystemEventStreamMessage | null>(location.state as any);
  const {
    data = {
      SHOPEE: [],
      LAZADA: [],
      TIKTOK: [],
      TIKTOK_US: [],
      MERCADO: [],
      KWAI: [],
    },
  } = useRequest(async () => {
    try {
      const {
        data: {
          data: {
            shopeeCountData: shopeeRegionCountsMap,
            lazadaCountData: lazadaRegionCountsMap,
            tiktokCountData: tiktokRegionCountsMap,
            tiktokUsCountData: tiktokUsRegionCountsMap = {} as RegionCountsMap,
            mercadoCountData: mercadoRegionCountsMap = {} as RegionCountsMap,
            kwaiCountData: kwaiRegionCountsMap = {} as RegionCountsMap,
          },
        },
      } = await getRegionsStoreCount();
      const SHOPEE = Object.keys(shopeeRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      const LAZADA = Object.keys(lazadaRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      const TIKTOK = Object.keys(tiktokRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      const TIKTOK_US = Object.keys(tiktokUsRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      const MERCADO = Object.keys(mercadoRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      const KWAI = Object.keys(kwaiRegionCountsMap).map(
        (key) => toUpperCase(key.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
      );
      return {
        SHOPEE,
        LAZADA,
        TIKTOK,
        TIKTOK_US,
        MERCADO,
        KWAI,
      };
    } catch (error) {
      return {
        SHOPEE: [],
        LAZADA: [],
        TIKTOK: [],
        TIKTOK_US: [],
        MERCADO: [],
        KWAI: [],
      };
    }
  });
  const [active, setActive] = useState<General.PlatformUpperCase>();
  const platforms = useMemo(() => {
    if (!data) {
      return [];
    }
    return Object.keys(data)
      .filter((x) => data[x].length > 0)
      .map((x) => toUpperCase(x));
  }, [data]);
  useEffect(() => {
    const statePlatform = locationStateRef.current?.content?.platformType as Exclude<
      General.PlatformUpperCase,
      'TOKOPEDIA' | 'ALIEXPRESS'
    >;
    if (statePlatform && platforms.includes(statePlatform)) {
      setActive(toUpperCase(statePlatform));
    } else {
      setActive(platforms[0]);
    }
  }, [platforms]);
  return {
    platforms,
    active,
    setActive,
  };
};

export default usePlatformList;
